import {
  Grid,
  makeStyles,
  Theme,
  Typography,
  useTheme,
} from '@material-ui/core';
import { useEffect, useRef } from 'react';

import { Content, Header, Page } from '@backstage/core-components';
import {
  SearchFilter,
  SearchPagination,
  useSearch,
} from '@backstage/plugin-search-react';
import { triggerDynatraceEvent } from '../../utils/trigger-dynatrace-events';
import {
  HandbookSearchResultListItem,
  CatalogSearchResultListItem,
  TechdocsSearchResultListItem,
  MarketplaceSearchResultListItem,
  CustomSearchResult,
} from '@internal/backstage-plugin-custom-search-ui-extension';

import BookIcon from '@mui/icons-material/Book';
import CategoryIcon from '@mui/icons-material/Category';
import SettingsIcon from '@mui/icons-material/Settings';
import { SearchTypeTabs } from './SearchTabs';
import Storefront from '@material-ui/icons/Storefront';
import { EmptyTermComponent } from './EmptyTermComponent';
import { useAnalytics } from '@backstage/core-plugin-api';
import { FontFamily } from '@principal-developer-portal/ui';
import { CustomSearchBar } from '@internal/backstage-plugin-custom-search-ui-extension';

// import { HomePageSearchBar } from '@backstage/plugin-search';

const useStyles = makeStyles((theme: Theme) => ({
  bar: {
    padding: theme.spacing(1, 0),
  },
  filters: {
    padding: theme.spacing(2),
    marginTop: theme.spacing(2),
    position: 'fixed',
    right: 0,
  },
  filter: {
    '& + &': {
      marginTop: theme.spacing(2),
    },
  },
  searchIcon: {
    // @ts-expect-error custom color
    color: theme.palette.searchIcon,
  },
  searchBar: {
    backgroundColor: theme.palette.background.paper,
    boxShadow: 'none',
    '& input': {
      fontSize: '18px',
      fontFamily: 'FS Elliot Pro',
    },
    '& input:-webkit-autofill': {
      '-webkit-box-shadow': `0 0 0 1000px ${theme.palette.background.paper} inset !important`,
      'box-shadow': `0 0 0 1000px ${theme.palette.background.paper} inset !important`,
      '-webkit-text-fill-color': `${theme.palette.text.primary} !important`,
      fontSize: '18px !important',
      fontFamily: 'FS Elliot Pro !important',
    },
    '& input:-webkit-autofill:hover': {
      '-webkit-box-shadow': `0 0 0 1000px ${theme.palette.background.paper} inset !important`,
      'box-shadow': `0 0 0 1000px ${theme.palette.background.paper} inset !important`,
      '-webkit-text-fill-color': `${theme.palette.text.primary} !important`,
      fontSize: '18px !important',
      fontFamily: 'FS Elliot Pro !important',
    },
    '& input:-webkit-autofill:focus': {
      '-webkit-box-shadow': `0 0 0 1000px ${theme.palette.background.paper} inset !important`,
      'box-shadow': `0 0 0 1000px ${theme.palette.background.paper} inset !important`,
      '-webkit-text-fill-color': `${theme.palette.text.primary} !important`,
      fontSize: '18px !important',
      fontFamily: 'FS Elliot Pro !important',
    },
    '& input:-webkit-autofill:active': {
      '-webkit-box-shadow': `0 0 0 1000px ${theme.palette.background.paper} inset !important`,
      'box-shadow': `0 0 0 1000px ${theme.palette.background.paper} inset !important`,
      '-webkit-text-fill-color': `${theme.palette.text.primary} !important`,
      fontSize: '18px !important',
      fontFamily: 'FS Elliot Pro !important',
    },
  },
}));

const usePrevious = (value: any) => {
  const ref = useRef();

  useEffect(() => {
    ref.current = value;
  });

  return ref.current;
};

const CustomSearchPage = () => {
  const classes = useStyles();
  const theme = useTheme();
  const { types, term, result, setFilters } = useSearch();
  const previousTab = usePrevious(types);
  const previousResult = usePrevious(result);
  const analytics = useAnalytics();

  useEffect(() => {
    analytics.captureEvent('search:visited', 'search');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const SearchHeader = () => {
    switch (types[0]) {
      case 'software-catalog':
        return (
          <>
            <Typography
              style={{ display: 'flex', padding: '20px 0px 0px 0px' }}
            >
              <Typography>
                <CategoryIcon className={classes.searchIcon} fontSize="large" />
              </Typography>
              <Typography
                variant="h6"
                style={{
                  paddingLeft: '5px',
                  marginTop: '-5px',
                  fontWeight: 'bold',
                }}
              >
                {' '}
                Software Catalog{' '}
              </Typography>
            </Typography>
          </>
        );
      case 'techdocs':
        return (
          <>
            <Typography
              style={{ display: 'flex', padding: '20px 0px 0px 0px' }}
            >
              <Typography>
                <SettingsIcon className={classes.searchIcon} fontSize="large" />
              </Typography>
              <Typography
                variant="h6"
                style={{
                  paddingLeft: '5px',
                  marginTop: '-5px',
                  fontWeight: 'bold',
                }}
              >
                {' '}
                Techdocs{' '}
              </Typography>
            </Typography>
          </>
        );
      case 'marketplace':
        return (
          <>
            <Typography
              style={{ display: 'flex', padding: '20px 0px 0px 0px' }}
            >
              <Typography>
                <Storefront className={classes.searchIcon} fontSize="large" />
              </Typography>
              <Typography
                variant="h6"
                style={{
                  paddingLeft: '5px',
                  marginTop: '-5px',
                  fontWeight: 'bold',
                }}
              >
                {' '}
                Marketplace{' '}
              </Typography>
            </Typography>
          </>
        );
      case 'handbook':
        return (
          <>
            <Typography
              style={{ display: 'flex', padding: '20px 0px 0px 0px' }}
            >
              <Typography>
                <BookIcon className={classes.searchIcon} fontSize="large" />
              </Typography>
              <Typography
                variant="h6"
                style={{
                  paddingLeft: '5px',
                  marginTop: '-5px',
                  fontWeight: 'bold',
                }}
              >
                {' '}
                Engineering Handbook{' '}
              </Typography>
            </Typography>
          </>
        );
      default:
        return null;
    }
  };

  useEffect(() => {
    let resultStatus;
    const resultData = result.value?.numberOfResults ?? 0;
    if (resultData > 0) {
      resultStatus = 'Success';
    } else {
      resultStatus = 'Failure';
    }
    const eventType: any = {
      search_status: {
        value: resultStatus,
        public: true,
      },
      search_term: {
        value: term,
        public: true,
      },
    };
    triggerDynatraceEvent(eventType, 'search_event');
  }, [term, result]);

  // whenever the search type changes, reset the filters
  useEffect(() => {
    setFilters({});

    if (previousTab) {
      // This also captures when the page loads with a tab in the query string
      // and so it may not be 100% accurate, but probably close enough.
      analytics.captureEvent('search:tab-changed', types?.[0] || 'all', {
        attributes: {
          from: previousTab?.[0] || 'all',
          to: types?.[0] || 'all',
        },
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [types]);

  const handleSearchEvent = () => {
    const eventType: any = {
      search_click_result: {
        value: 'total_result_clicks',
        public: true,
      },
    };
    triggerDynatraceEvent(eventType, 'click_on_search_result_event');
  };

  const handleResultClicked = (event: any) => {
    analytics.captureEvent('search:result-clicked', event.location, {
      attributes: {
        ...event,
        tab: types?.[0] || 'all',
        // TODO: If we ever need to, we could truncate event.text here.
      },
    });
  };

  useEffect(() => {
    // Only send event when term exist, previous result was loading and now is not (meaning that the search just returned).
    if (
      term &&
      (previousResult as any)?.loading === true &&
      result?.loading === false
    ) {
      analytics.captureEvent('search:results', term, {
        attributes: {
          numberOfResults: result.value?.results?.length || 0,
          results: JSON.stringify(
            result.value?.results?.map(_result => ({
              rank: _result.rank,
              title: _result.document.title,
              location: _result.document.location,
            })) || [],
          ),
          tab: types?.[0] || 'all',
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [result, term]);

  return (
    <Page themeId="home">
      <Header title="Search" />
      <Content>
        <Grid container direction="row">
          <Grid item xs={12} md={7}>
            <CustomSearchBar
              className={classes.searchBar}
              searchTerm={term}
              // eslint-disable-next-line
              autoFocus
            />
            <Typography style={{ color: theme.palette.grey[700] }}>
              Search for specific phrases by wrapping them in double quotes.
            </Typography>
          </Grid>
          {/* Feedback on PDP search */}
          <Grid item xs={12} md={5} style={{ paddingTop: '20px' }}>
            <Typography
              style={{
                display: 'flex',
                float: 'right',
                padding: '4px 16px',
                backgroundColor: '#002855',
                color: 'white',
                borderRadius: '30px',
                fontFamily: FontFamily.Bold,
                cursor: 'pointer',
                marginRight: '20px',
              }}
              onClick={() => {
                window.open(
                  'https://forms.office.com/pages/responsepage.aspx?id=jEfqO4QWjEqOhQRexUukMKSpA55WN-9ErfvgpnFVeCFUQTRIOExFTVdHRlcwR0VUS1hCNzFDOFdTNi4u&route=shorturl',
                  '_blank',
                );
              }}
            >
              <Typography style={{ display: 'inline', paddingTop: '5px' }}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="2em"
                  height="2em"
                  viewBox="0 0 24 24"
                >
                  <path
                    fill="#DCF94D"
                    d="M9 22c-.6 0-1-.4-1-1v-3H4c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2h-6.1l-3.7 3.7c-.2.2-.4.3-.7.3zm1-6v3.1l3.1-3.1H20V4H4v12zm6.3-10l-1.4 3H17v4h-4V8.8L14.3 6zm-6 0L8.9 9H11v4H7V8.8L8.3 6z"
                  />
                </svg>
              </Typography>
              <Typography style={{ display: 'inline', padding: '8px' }}>
                Provide feedback on PDP search
              </Typography>
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <SearchTypeTabs
              types={[
                {
                  value: 'handbook',
                  name: 'Engineering Handbook',
                },
                {
                  value: 'software-catalog',
                  name: 'Software Catalog',
                },
                {
                  value: 'techdocs',
                  name: 'TechDocs',
                },
                {
                  value: 'marketplace',
                  name: 'Marketplace',
                },
              ]}
              defaultValue="handbook"
            />
            {term && <SearchHeader />}
          </Grid>
          {!term && !result.loading ? (
            <Grid item xs={9}>
              <EmptyTermComponent />
            </Grid>
          ) : (
            <>
              <Grid item xs={9}>
                <CustomSearchResult onClick={handleSearchEvent}>
                  <HandbookSearchResultListItem
                    icon={
                      <BookIcon
                        className={classes.searchIcon}
                        fontSize="large"
                      />
                    }
                    type={types}
                    onClick={handleResultClicked}
                  />
                  <CatalogSearchResultListItem
                    icon={
                      <CategoryIcon
                        className={classes.searchIcon}
                        fontSize="large"
                      />
                    }
                    type={types}
                    onClick={handleResultClicked}
                  />
                  <TechdocsSearchResultListItem
                    icon={
                      <SettingsIcon
                        className={classes.searchIcon}
                        fontSize="large"
                      />
                    }
                    type={types}
                    onClick={handleResultClicked}
                  />
                  <MarketplaceSearchResultListItem
                    icon={
                      <Storefront
                        className={classes.searchIcon}
                        fontSize="large"
                      />
                    }
                    type={types}
                    onClick={handleResultClicked}
                  />
                </CustomSearchResult>
                {result.loading === false && <SearchPagination />}
              </Grid>
              <Grid style={{ marginLeft: '35px' }}>
                {types.includes('software-catalog') && (
                  <>
                    <SearchFilter.Checkbox
                      // @ts-ignore
                      label={
                        <Typography
                          variant="body1"
                          style={{ fontWeight: 'bold' }}
                        >
                          Kind
                        </Typography>
                      }
                      name="kind"
                      values={[
                        'Component',
                        'Template',
                        'Resource',
                        'Group',
                        'User',
                        'API',
                      ]}
                    />
                    <SearchFilter.Checkbox
                      // @ts-ignore
                      label={
                        <Typography
                          variant="body1"
                          style={{ fontWeight: 'bold' }}
                        >
                          Lifecycle
                        </Typography>
                      }
                      name="lifecycle"
                      values={['experimental', 'production']}
                    />
                  </>
                )}
              </Grid>
            </>
          )}
        </Grid>
      </Content>
    </Page>
  );
};

export const customSearchPage = <CustomSearchPage />;
